import { signIn, signOut, confirmSignIn, getCurrentUser, fetchUserAttributes, resetPassword, confirmResetPassword } from "aws-amplify/auth";
import React, { createContext, useContext, useEffect, useState } from "react";
import restUtils from "core/utils/rest";

export type MfaType = "SMS_MFA" | "SOFTWARE_TOKEN_MFA" | null;

interface UseAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  username: string;
  attributes: {
    [key: string]: any;
  };
  signIn: (username: string, password: string) => Promise<Result>;
  signOut: () => void;
  verifyTotp: (totp: string) => Promise<Result>;
  currentChallenge: string | null;
  forgotPassword: (username: string) => Promise<Result>;
  confirmNewPassword: (username: string, code: string, newPassword: string) => Promise<Result>;
}

interface Result {
  success: boolean;
  message: string;
  path?: string;
}

type Props = {
  children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

export const ProvideAuth: React.FC<Props> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [currentChallenge, setCurrentChallenge] = useState<null|string>(null);
  const [attributes, setAttributes] = useState({});

  const forgotPassword = async (username: string) => {
    console.log("Calling resetPassword with username:", username);
    try {
      const response = await resetPassword({ username });
      console.log("Reset response:", response);
      return { success: true, message: "Code sent if user exists" };
    } catch (error: any) {
      console.error("Reset error:", error);
      return { success: false, message: error.message || "Unable to verify user" };
    }
  };
  const confirmNewPassword = async (username: string, code: string, newPassword: string) => {
    try {
      await confirmResetPassword( { username, newPassword,  confirmationCode: code } );
      return { success: true, message: "Password reset" };
    } catch (error: any) {
      return { success: false, message: error.message || "Validation error" };
    }
  };

  const validateUserRole = async (attributes: { [key: string]: any }) => {
    const roleCode = attributes["custom:role"] as string;   // ← now it's the short code
    if (!roleCode) {
      throw new Error("No role assigned to user");
    }

    try {
      const response = await restUtils.call<any>("UserManagementApi", "user-roles", "GET", { pageSize: 1000 });
      const validRoleCodes = response?.data?.map((r: { role_code: string }) => r.role_code) || [];

      if (!validRoleCodes.includes(roleCode)) {
        console.log("Invalid role code:", roleCode);
        await signOut();
        throw new Error("Invalid role");
      }
    } catch (error) {
      console.error("Role validation failed:", error);
      await signOut();
      throw new Error("Role validation error");
    }
  };

  const setCurrentUser = async () => {
    try {
      const [user, attrs] = await Promise.all([getCurrentUser(), fetchUserAttributes()]);
      await validateUserRole(attrs);
      setUsername(user.username);
      setAttributes(attrs);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("setCurrentUser error:", error);
      setUsername("");
      setAttributes({});
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setCurrentUser();
  }, []);

  const handleSignIn = async (username: string, password: string) => {
    try {
      const { isSignedIn, nextStep } = await signIn({ username, password });
      if (!isSignedIn) {
        setCurrentChallenge(nextStep.signInStep);
        return { success: true, message: "TOTP", path: "/auth/verify" };
      } else {
        await setCurrentUser(); // This will fetch attributes and validate role
        setCurrentChallenge(null);
        return { success: true, message: "", path: "/" };
      }
    } catch (error) {
      console.error("Sign in error:", error);
      return { success: false, message: "LOGIN FAILED" };
    }
  };

  const verifyTotp = async (code: string) => {
    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: code });
      if (isSignedIn) {
        await setCurrentUser(); // Validate after confirm
      }
      return { success: true, message: "" };
    } catch (error) {
      console.error("Verify TOTP error:", error);
      setUsername("");
      setAttributes({});
      setIsAuthenticated(false);
      return { success: false, message: "VERIFY TOTP FAIL" };
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUsername("");
      setAttributes({});
      setIsAuthenticated(false);
      return { success: true, message: "" };
    } catch (error) {
      console.error("Sign out error:", error);
      return { success: false, message: "LOGOUT FAIL" };
    }
  };

  return {
    isLoading,
    isAuthenticated,
    attributes,
    username,
    verifyTotp,
    signOut: handleSignOut,
    signIn: handleSignIn,
    currentChallenge,
    forgotPassword,
    confirmNewPassword
  };
};

export default {
  useAuth,
  ProvideAuth,
};
